import {useDispatch} from "react-redux";
import {useNavigate} from "react-router-dom";
import {useEffect, useState} from "react";
import {Order} from "../../../../../models/Order";
import {toast} from "react-toastify";
import {
    CreateSupplierOrder,
    GetEmployeeProviderLists
} from "../../../../../reduxToolkit/EmployeeProviders/EmployeeProvidersApi";
import {t} from "i18next";
import routes from "../../../../../Routes/routes";
import {errorMsg} from "../../../../../Apis/functions";
import HandleChange from "../../../../../Helpers/HandleChange";

/**
 * @email azapata@ibl.mx
 * @author Ferro Zapata Arnulfo
 * @jobTitle Full Stack Developer
 */

/**
 * Initializes and returns an object with utilities for managing an order.
 *
 *   Function to handle changes to the delivery date.
 *   Setter to update the order state; accepts either a new order object or a function that receives the previous state and returns the next.
 *   Clears all fields in the order form.
 *   True if one or more required fields are empty; false otherwise.
 *   Function or data used to retrieve lists of employees and providers.
 *   Toggles the `isDelivery` flag on the order.
 *   Handles the creation (or submission) of the order; may be an async function returning a Promise.
 *   The current order object containing all order details.
 *
 * @param {string} order.item
 *   The name or description of the item being ordered.
 * @param {string} order.supplierId
 *   Identifier of the selected supplier.
 * @param {function(): boolean} order.isValid
 *   Function that returns true if the order object passes validation rules.
 * @param {string} order.description
 *   Additional description or notes for the order.
 * @param {function(): {
 *     isDelivery: boolean,
 *     item: string,
 *     supplierId: string,
 *     folio: string|null,
 *     description: string|null,
 *     id: string|null,
 *     deliveryDate: Date|null
 *   }} order.toCreateUpdate
 *   Transforms the internal order state into the payload shape required for creation or update.
 * @param {boolean} order.isDelivery
 *   True if this order is a delivery; false if it is pickup.
 * @param {*} order.eta
 *   Estimated time of arrival (if applicable).
 * @param {*} order.statusId
 *   Current workflow status identifier.
 * @param {*} order.userReceivedId
 *   Identifier of the user who received the order.
 * @param {string} order.folio Folio or reference number for the order.
 * @param {*} order.supplierEmployeeId
 *   Identifier of the specific supplier employee handling this order.
 * @param {*} order.supplierVehicleColor
 *   Color of the vehicle used by the supplier (for deliveries).
 * @param {*} order.supplierVehicleId
 *   Identifier of the supplier’s vehicle.
 * @param {*} order.id
 *   Internal identifier for the order (null for new orders).
 * @param {Date|null} order.deliveryDate
 *   The scheduled delivery date, or null if not set.
 * @constructor
 */

export const EmployeeFlowCreateOrderApp =()=>{
    const dispatch = useDispatch();
    let navigate = useNavigate();
    const [emptyFields, setEmptyFields] = useState(false);
    const [order, setOrder] = useState({...Order});
    const { handleChange } = HandleChange({ model: order, setModel: setOrder });

    const CreateOrderHandler = async () => {
        if (!order.isValid()) {
            setEmptyFields(true);
            return;
        }
        setEmptyFields(false);
        try {
            const {
                payload: { data: { success, code } },
            } = await dispatch(CreateSupplierOrder(order.toRegister()));

            if (success) {
                toast.success(t('has_been_created_successfully'));
                navigate(routes.suppliers.main);
            } else errorMsg(code);
        } catch (error) {
            toast.error(t('network_error'));
        }
    };

    useEffect(() => {
        dispatch(GetEmployeeProviderLists());
    }, []);

    const handleClear = () => {
        setOrder({ ...Order});
    };

    return {
        CreateOrderHandler,
        handleClear,
        order,
        emptyFields,
        handleChange
    }
}
